export default `# Movies Pagination

Our application is up and running, but displays only the first page of the list of movies that meet the selected criteria.

It would be great if we could browse more than 20 movies. For that, we need to implement pagination.

We will place two buttons below the list of movies: one to move forward to the next page and one to move back.
We also have to keep track of which page we are currently viewing.

So far, the \`moviesUrl\` which we generated in the \`generateUrl\` method in the \`Main\` component,
has only used \`page=1\`. Let's add the new property, \`page\`, to the \`state\` of the \`Main\` component and
let's initialise it with the value \`1\`.

\`\`\`plainJsx
// Main.js

class Main extends React.Component {
  state = {
    page: 1,
    url: \`https://api.themoviedb.org/3/genre/movie/list?api_key=651925d45022d1ae658063b443c99784&language=en-US\`,
    // omitted code
  }

  // omitted code
}
\`\`\`

Now, we can replace the fixed page value in the \`generateUrl\` method with the actual value from the \`state\`:

\`\`\`plainJsx
// Main.js

class Main extends React.Component {
  // omitted code

  generateUrl = () => {
    const {genres, year, rating, runtime, page } = this.state;
    const selectedGenre = genres.find( genre => genre.name === this.state.genre);
    const genreId = selectedGenre.id;

    const moviesUrl = \`https://api.themoviedb.org/3/discover/movie?\` +
      \`api_key=651925d45022d1ae658063b443c99784&\` +
      \`language=en-US&sort_by=popularity.desc&\` +
      \`with_genres=\${genreId}&\` +
      \`primary_release_date.gte=\${year.value.min}-01-01&\` +
      \`primary_release_date.lte=\${year.value.max}-12-31&\` +
      \`vote_average.gte=\${rating.value.min}&\` +
      \`vote_average.lte=\${rating.value.max}&\` +
      \`with_runtime.gte=\${runtime.value.min}&\` +
      \`with_runtime.lte=\${runtime.value.max}&\` +
      \`page=\${page}\`;

    this.setState({ moviesUrl });
  }

  // omitted code
}
\`\`\`

Now, we have to implement methods that will be responsible for increasing and decreasing the \`page\` value in the \`state\`. But before we do so,
we first need to fix a tiny problem.

Imagine that we want to see a page with next 20 movies. To do so, we will click the "Next" button. But how do we know which page is the last one?

When the \`Movies\` component fetches the movies, the TMDB API sends back the \`data\`. One of the properties of the \`data\` object is \`total_pages\`.
When we are on the last page – where the \`page\` value is the same as the \`total_pages\` value – we should hide the "Next" button.

The problem that we have now is that the \`page\` value is stored in the \`state\` of the \`Main\` component. And it's the \`Movies\` component that
receives the information about the \`total_pages\`. If we want to implement methods that will increase the \`page\` value in the \`Main\` component,
it would be great if we could check when we reach the last page.

There is a quick fix to this problem. We just need to move the fetching movies operation to the \`Main\` component. Then, we will also store movies in the \`state\`
of the \`Main\` component. And – finally – we can pass the movies data to the \`Movies\`.

Here are the steps:
1. Move the \`fetchMovies\` and \`storeMovies\` methods from the \`Movies\` component to the \`Main\` component
2. Add \`movies\` to the \`Main\` component state and initialise it with an empty array
3. Remove the \`componentDidMount\` and \`componentWillReceiveProps\` methods from the \`Movies\` component
4. Change the \`Movies\` into a functional component that will receive \`movies\` via props:

\`\`\`plainJsx
// Movies.js

import React from "react";
import "./Movies.css";
import MovieListItem from "./MovieListItem";

const Movies = ({movies}) => (
  <section>
    <ul className="movies">
      {movies.map( movie => (
        <MovieListItem key={movie.id} movie={movie} />
      ))}
    </ul>
  </section>
)

export default Movies;
\`\`\`

Add the \`componentDidMount\` method to the \`Main\` component and fetch movies there:

\`\`\`plainJsx
// Main.js

componentDidMount() {
  this.fetchMovies(this.state.moviesUrl);
}
\`\`\`

Now, when the \`Main\` component renders for the first time, it will fetch movies just like the \`Movies\` component used to do.

Don't forget to pass the \`movies\` array in the *Main.js* down to the \`Movies\`:
\`\`\`plainJsx
// Main.js

<Movies movies={this.state.movies}/>
\`\`\`

We have also to make sure that we re-fetch the movies when we press the \`Search\` button.

When we press the \`Search\` button, a new \`moviesUrl\` is created by the \`generateUrl\` method and stored in the \`state\` of the \`Main\` component.

React gives us another lifecycle method, where we can react to changes in the \`state\` – the \`componentWillUpdate\` method. This is where we can
re-fetch the movies if the new \`moviesUrl\` is different from the old one. The \`componenWillUpdate\` receives two arguments – \`nextProps\` and \`nextState\`.

That's how we can compare the \`this.state.moviesUrl\` with the \`nextState.moviesUrl\`.

Add the \`componentWillUpdate\` to the \`Main\` component:

\`\`\`plainJsx
// Main.js

componentWillUpdate(nextProps, nextState) {
  if (this.state.moviesUrl !== nextState.moviesUrl) {
    this.fetchMovies(nextState.moviesUrl);
  }
}
\`\`\`

Finally, let's store the \`total_pages\` for the movies list in the \`state\`.
1. Add \`total_pages\` to the state and initialise it with \`1\` – it will be updated when the \`Main\` component mounts
2. Update the \`storeMovies\` method so that it stores \`total_pages\` in the \`state\`

\`\`\`plainJsx
// Main.js

storeMovies = data => {
  const movies = data.results.map(result => {
    const {
      vote_count,
      id,
      genre_ids,
      poster_path,
      title,
      vote_average,
      release_date
    } = result;
    return { vote_count, id, genre_ids, poster_path, title, vote_average, release_date };
  });
  this.setState({ movies, total_pages: data.total_pages });
};
\`\`\`

Here is a refactored application with \`Movies\` as a functional component:
\`\`\`codeSandbox
m4k62vroq8
\`\`\`

## Pagination

We are ready to write methods to move to the next and previous page:

\`\`\`plainJsx
// Main.js

onPageIncrease = () => {
  const { page, total_pages } = this.state
  const nextPage = page + 1;
  if (nextPage <= total_pages) {
    this.setState({ page: nextPage })
  }
}

onPageDecrease = () => {
  const nextPage = page - 1;
  if ( nextPage > 0 ) {
    this.setState({ page: nextPage })
  }
}
\`\`\`

The \`onPageIncrease\` method updates the \`page\` value in the \`state\` only if the next page exists.
The \`onPageDecrease\` method updates the \`page\` value only if it's still positive. We don't want to browse page number \`0\` or \`-1\`

We also want to re-fetch movies everytime the \`page\` changes. We could call the \`this.generateUrl\` method in both \`onPageIncrease\` and \`onPageDecrease\`.
But React does not always apply the changes to the \`state\` immediately. So even if we called the \`generateUrl\` after the \`this.setState({page: nextPage})\`, we
could still end up with an url of the previous page.

That's why we should use the \`componentWillUpdate\` method to react to changes in the \`state\`. Let's add the condition:
\`\`\`plainJsx
// Main.js

componentWillUpdate(nextProps, nextState) {
  if (this.state.moviesUrl !== nextState.moviesUrl) {
    this.fetchMovies(nextState.moviesUrl);
  }
  if (this.state.page !== nextState.page) {
    this.generateUrl();
  }
}
\`\`\`

Now, when React updates the \`page\` value in the \`state\` the \`componentWillUpdate\` method will call the \`generateUrl\` method.
The \`generateUrl\` method will update the \`state\` again by creating a new \`moviesUrl\`. And that, in turn, will trigger \`fetchMovies\` in
the \`componentWillUpdate\` method.

Finally, we are ready to pass both methods to the \`Movies\` component and add the \`Previous\` and \`Next\` buttons.

Let's pass both methods and the \`page\` value to the \`Movies\`:

\`\`\`plainJsx
// Main.js

class Main extends React.Component {
  // omitted code
  render() {
    return (
      <section className="main">
        <Navigation
          onChange={this.onChange}
          onGenreChange={this.onGenreChange}
          setGenres={this.setGenres}
          onSearchButtonClick={this.onSearchButtonClick}
          {...this.state} />
        <Movies
          movies={this.state.movies}
          page={this.state.page}
          onPageIncrease={this.onPageIncrease}
          onPageDecrease={this.onPageDecrease}
        />
      </section>
    )
  }
}
\`\`\`

And let's create buttons. But we have already implemented the \`SearchButton\`. Let's change its name to \`Button\`. You should also change the file name to *Button.js*.
For the sake of consistency, let's also change the name of the CSS file from *SearchButton.css* to *Button.css*.

React always passes \`children\` property in \`props\`. The \`children\` property is everything that you put between an opening and closing tag of the component.

Instead of placing a fixed "Search" text in the declaration of the \`Button\` component, we can display it using the \`children\` property from \`props\`:

\`\`\`plainJsx
// Button.js

import React from "react";
import "./Button.css"

const Button = ({ onClick, children }) => (
  <div className="search-button">
    <button onClick={onClick}>
      {children}
    </button>
  </div>
)

export default Button;
\`\`\`

We also have to adjust, the way we display the \`Button\` component in the \`Navigation\`. Replace the old code:

\`\`\`plainJsx
// Navigation.js

import SearchButton from "./SearchButton";

<SearchButton onClick={onSearchButtonClick}/>
\`\`\`

with the new version:
\`\`\`plainJsx
// Navigation.js

import Button from "./Button";

<Button onClick={onSearchButtonClick}/>
  Search
</Button>
\`\`\`


Now, we are ready to use the same \`Button\` component in the \`Movies\`. We will just pass it a different \`onClick\` method.

Let's wrap both buttons and the text displaying the page number in a \`div\` with the class \`pagination\`:

\`\`\`plainJsx
// Movies.js

import React from "react";
import "./Movies.css";
import MovieListItem from "./MovieListItem";
import Button from "../navigation/Button";

const Movies = ({
  movies,
  page,
  onPageIncrease,
  onPageDecrease
}) => (
  <section>
    <ul className="movies">
      {movies.map( movie => (
        <MovieListItem key={movie.id} movie={movie} />
      ))}
    </ul>
    <div className="pagination">
      <Button onClick={onPageDecrease}>Previous</Button>
      <span>{\`Page \${page}\`}</span>
      <Button onClick={onPageIncrease}>Next</Button>
    </div>
  </section>
)

export default Movies;
\`\`\`

Add also the \`.pagination\` class to the *Movies.css* file:

\`\`\`plainCss
/* Movies.css */

.pagination {
  display: flex;
  justify-content: space-between;
  padding: 40px 20px;
}
\`\`\`

Our pagination works perfectly now. But when go to, say, the third page of a given listing and then change search criteria,
we will fetch the third – rather than the first – page for the new criteria. That's because we have not reset the \`page\` to \`1\`.

To fix this, we need to reset the \`page\` to \`1\` before we generate a new url in the \`onSearchButtonClick\` method:

\`\`\`
// Main.js

onSearchButtonClick = () => {
  this.setState({page: 1});
  this.generateUrl();
}
\`\`\`

Now, even if we are on page 15 and we change search criteria, the \`page\` value will be set to \`1\`.

Here is the final code:

\`\`\`codeSandbox
zknj00qwp3
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/movie-pages-navigation.js